import {Notification, NotificationType} from '@lib/store';
import {AppLanguage, formatDate, formatDateString, formatDateOptions} from '@lib/utils';
import {formatDistanceToNow, isSameDay, parseISO} from 'date-fns';
import {TFunction} from 'i18next';

export const useTransformNotification = <
  TCallBack extends (type: NotificationType) => void,
  TCallBackReturnType = ReturnType<TCallBack>,
>(
  notification: Notification,
  getIconCallback: TCallBack,
  t: TFunction,
  appLanguage: AppLanguage,
) => {
  const {messageValues, type, isRead} = notification;

  const timeFormat = 'HH:mm';
  const fullDateTimeFormat = 'd MMMM HH:mm';
  const parsedMeetingStartDate = messageValues.StartDateTimeUtc && parseISO(messageValues.StartDateTimeUtc);
  const parsedMeetingEndDate = messageValues.EndDateTimeUtc && parseISO(messageValues.EndDateTimeUtc);
  const endDateFormat =
    parsedMeetingStartDate && parsedMeetingEndDate && isSameDay(parsedMeetingStartDate, parsedMeetingEndDate)
      ? timeFormat
      : fullDateTimeFormat;

  const dateRange =
    parsedMeetingStartDate && parsedMeetingEndDate
      ? `${formatDate(parsedMeetingStartDate, fullDateTimeFormat, appLanguage)} - ${formatDate(
          parsedMeetingEndDate,
          endDateFormat,
          appLanguage,
        )}`
      : undefined;

  const referenceDateString = messageValues.ShiftDate ?? messageValues.StartDateTimeUtc;
  const referenceDate = referenceDateString ? parseISO(referenceDateString) : undefined;

  const transformedMessageValues = {
    AdminName: messageValues.AdminName ?? '',
    BuildingName: messageValues.BuildingName ?? '',
    ShiftDate: messageValues.ShiftDate && formatDateString('EEEE d MMMM', appLanguage)(messageValues.ShiftDate),
    OrganizerName: messageValues.OrganizerName ?? '',
    InviteeCount: messageValues.InviteeCount ?? 0,
    MeetingRoomName: messageValues.MeetingRoomName ?? '',
    MinutesUntilRoomIsReleased: messageValues.MinutesUntilRoomIsReleased ?? '',
    DateRange: dateRange,
    ReferenceDate: referenceDate,
  };

  const timeAgoText = formatDistanceToNow(parseISO(notification.creationDateTime), {
    ...formatDateOptions(appLanguage),
    addSuffix: true,
  });

  const _baseProperties = {
    notificationType: type,
    transformedMessageValues,
    timeAgoText,
    isRead,
    icon: getIconCallback(type) as TCallBackReturnType,
  };

  switch (notification.type) {
    case 'NotifyBookingOnBehalf':
      return {
        title: t('notifications:NotifyBookingOnBehalfTitle'),
        message: t('notifications:NotifyBookingOnBehalfMessage', transformedMessageValues),
        ..._baseProperties,
      };
    case 'NotifyUpdateOnBehalf':
      return {
        title: t('notifications:NotifyUpdateOnBehalfTitle'),
        message: t('notifications:NotifyUpdateOnBehalfMessage', transformedMessageValues),
        ..._baseProperties,
      };
    case 'NotifyDeleteOnBehalf':
      return {
        title: t('notifications:NotifyDeleteOnBehalfTitle'),
        message: t('notifications:NotifyDeleteOnBehalfMessage', transformedMessageValues),
        ..._baseProperties,
      };
    case 'NotifyDeleteOnLocationClose':
      return {
        title: t('notifications:NotifyDeleteOnLocationCloseTitle'),
        message: t('notifications:NotifyDeleteOnLocationCloseMessage', transformedMessageValues),
        ..._baseProperties,
      };
    case 'NotifyInAppReservationInvitationCreated':
      const inviteeCountWithoutUser = transformedMessageValues.InviteeCount - 1;

      return {
        title: t('notifications:NotifyInAppReservationInvitationCreatedTitle'),
        message:
          inviteeCountWithoutUser <= 0
            ? t('notifications:NotifyInAppReservationInvitationCreatedMessage', transformedMessageValues)
            : t('notifications:NotifyInAppReservationInvitationWithOtherInviteeCreatedMessage', {
                ...transformedMessageValues,
                count: inviteeCountWithoutUser,
              }),
        ..._baseProperties,
      };
    case 'NotifyGroupInvitationCanceled':
      return {
        title: t('notifications:NotifyGroupInvitationCanceledTitle'),
        message: t('notifications:NotifyGroupInvitationCanceledMessage', transformedMessageValues),
        ..._baseProperties,
      };
    case 'NotifyReservationReminder':
      return {
        title: t('notifications:NotifyReservationReminderTitle'),
        message: t('notifications:NotifyReservationReminderMessage', transformedMessageValues),
        ..._baseProperties,
      };
    case 'NotifyNoShowDetected':
      return {
        title: t('notifications:NotifyNoShowDetectedTitle'),
        message: t('notifications:NotifyNoShowDetectedMessage', transformedMessageValues),
        ..._baseProperties,
      };
    case 'NotifyNoShowRoomReleased':
      return {
        title: t('notifications:NotifyNoShowRoomReleasedTitle', transformedMessageValues),
        message: t('notifications:NotifyNoShowRoomReleasedMessage'),
        ..._baseProperties,
      };
    default:
      return {
        title: notification.messageTitle,
        message: notification.message,
        ..._baseProperties,
      };
  }
};
